import { getObjectKeys } from "@avvoka/shared";
import { computed, inject, unref } from "vue";
import type { ThemeObject, ThemeProp } from "./types";

interface ITheme {
  [T: string]: string | boolean | ITheme
}

function mergeTheme <T extends ITheme> (...objects: T[]): T {
  const isObject = (obj: string | boolean | ITheme): obj is ITheme => !!obj && typeof obj === 'object'

  return objects.reduce((prev, obj) => {
    getObjectKeys<T>(obj).forEach(key => {
      const pVal = prev[key]
      const oVal = obj[key]

      if (isObject(pVal) && isObject(oVal)) {
        prev[key] = mergeTheme(pVal, oVal)
      } else {
        prev[key] = oVal
      }
    })

    return prev
  }, {} as T)
}

const ThemeRegistry: Map<string, ThemeObject> = new Map()

/**
 * Used by VTheme to provide theme to whole subtree of components
 */
export const ThemeInjectionKey = 'V:VTheme:Key'

export function declareTheme (themeDeclaration: ThemeObject) {
  return (name: string) => {
    ThemeRegistry.set(name, themeDeclaration)
  }
}

export function useTheme <Component extends keyof ThemeObject> (
  props: { theme?: ThemeProp<Component> },
  componentName: Component
) {
  /**
   * Fetch first theme (or theme provided by VTheme) to be the default theme
   */
  const defaultTheme = ThemeRegistry.get(unref(inject(ThemeInjectionKey, Array.from(ThemeRegistry.keys())[0])))!

  const resolveTheme = (theme?: ThemeProp<Component>) => {
    if (typeof theme === 'string') {
      return (ThemeRegistry.get(theme) ?? defaultTheme)[componentName]
    } else if (theme) {
      return mergeTheme(defaultTheme[componentName], theme as ThemeObject[Component])
    } else {
      return defaultTheme[componentName]
    } 
  }

  return computed(() => ({
    ...resolveTheme(props.theme),
    __key: typeof props.theme === 'string' ? props.theme : 'default'
  }))
}
